import type { ChatMessage } from '../chatSlice'
import type { StudioJob, Gate } from '../studioTypes'
import { serverTimeMs } from './serverTime'

export type ChatItem =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'event'; id: string; stage: string; text: string }
  /** Вопрос прогона. Несёт гейт целиком: карточке нужны и варианты, и
   *  состояние, и срок, а разбирать их дважды незачем. */
  | { kind: 'gate'; id: string; gate: Gate }

/** Непарсибельная метка не должна ни ронять ленту, ни притворяться нулём
 *  (иначе запись уехала бы в 1970 год и встала первой). Отправляем в конец. */
const LAST = Number.MAX_SAFE_INTEGER

/**
 * Лента чата = реплики автора (наш слайс) + события прогона (лента джобы) + вопросы.
 *
 * Ключ события — `индекс` + `at` + текст. Пары `at`+текст НЕ хватает (M-5):
 * батч уроков укладывается в одну секунду, и два события с одинаковой меткой и
 * одинаковым текстом — реалистичный случай, а одинаковый React-key на двух
 * соседних строках ломает список. Индекс САМ ПО СЕБЕ ключом быть не может:
 * окно на сервере скользящее (сто последних, старые молча удаляются с начала),
 * и голый индекс переставлял бы половину списка на каждом выпадении. По той же
 * причине лента перерисовывается целиком, а не дописывается.
 *
 * Лента прогона = события + вопросы, слитые по времени. Вопрос в `events`
 * не приезжает намеренно (окно в сто записей его бы потеряло), поэтому
 * второй источник — не дубль, а единственный способ увидеть диалог целиком.
 * Сравниваем ЧИСЛАМИ через serverTimeMs: у событий метка со смещением, у
 * гейтов — без зоны вовсе, и сравнение строк тут врало бы.
 *
 * `stage` и `kind` — свободные строки, не перечисления: отдаём как есть, логику
 * по ним не ветвим.
 */
export const buildChatItems = (userMessages: ChatMessage[], job: StudioJob | null): ChatItem[] => {
  const items: ChatItem[] = userMessages.map((m) => ({
    kind: 'user',
    id: m.id,
    text: m.text,
  }))

  const timed: { at: number; item: ChatItem }[] = []
  for (const [index, event] of (job?.result?.events ?? []).entries()) {
    timed.push({
      at: serverTimeMs(event.at) ?? LAST,
      item: {
        kind: 'event',
        id: `${index}:${event.at}:${event.message}`,
        stage: event.stage,
        text: event.message,
      },
    })
  }
  for (const gate of job?.gates ?? []) {
    // Ключ — id гейта: он стабилен, в отличие от индекса события.
    timed.push({
      at: serverTimeMs(gate.asked_at) ?? LAST,
      item: { kind: 'gate', id: `gate:${gate.id}`, gate },
    })
  }
  // Сортировка стабильная (Array.prototype.sort в ES2019+): записи с
  // одинаковой меткой сохраняют порядок, в котором их отдал сервер.
  timed.sort((a, b) => a.at - b.at)

  return [...items, ...timed.map((t) => t.item)]
}
